import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CustomerDownloadStatus } from './offline-model';
import { RootState } from '../root/root-types';

export interface DownloadStatusState {
  catalogDownloadStatus: Record<string, CustomerDownloadStatus>;
  allCustomerProfileSynced: boolean;
  allCustomerGroupProfileSynced: boolean;
}

export const DOWNLOAD_STATUS_FEATURE_KEY = 'downloadStatus';

export const initialDownloadStatusState: DownloadStatusState = {
  catalogDownloadStatus: {},
  allCustomerProfileSynced: false,
  allCustomerGroupProfileSynced: false,
};

// ─── Slice ────────────────────────────────────────────────────────────────────

export const downloadStatusSlice = createSlice({
  name: DOWNLOAD_STATUS_FEATURE_KEY,
  initialState: initialDownloadStatusState,
  reducers: {
    addCatalogDownloadItems: (state, action: PayloadAction<string[]>) => {
      state.catalogDownloadStatus = {
        ...state.catalogDownloadStatus,
        ...action.payload.reduce(
          (acc, customerId) => {
            // Preserve existing status if already tracked, else initialise
            acc[customerId] = state.catalogDownloadStatus[customerId]
              ? { ...state.catalogDownloadStatus[customerId] }
              : { catalog: false, profile: false };
            return acc;
          },
          {} as Record<string, CustomerDownloadStatus>
        ),
      };
    },

    updateCatalogDownloadStatus: (
      state,
      action: PayloadAction<{ customerId: string; status: boolean }>
    ) => {
      const { customerId, status } = action.payload;
      if (state.catalogDownloadStatus[customerId]) {
        state.catalogDownloadStatus[customerId].catalog = status;
      }
    },

    updateCustomerProfileDownloadStatus: (
      state,
      action: PayloadAction<{ customerId: string; status: boolean }>
    ) => {
      const { customerId, status } = action.payload;
      if (state.catalogDownloadStatus[customerId]) {
        state.catalogDownloadStatus[customerId].profile = status;
      }
    },

    updateAllCustomerProfileSynced: (state, action: PayloadAction<boolean>) => {
      state.allCustomerProfileSynced = action.payload;
    },

    updateAllCustomerGroupProfileSynced: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.allCustomerGroupProfileSynced = action.payload;
    },

    /**
     * Called during cache integrity check.
     * Resets stale Redux flags that no longer have a matching cache entry.
     * Profile is reset then auto re-fetched; catalog is reset for manual re-download.
     */
    resetDownloadStatusForCustomers: (
      state,
      action: PayloadAction<{
        profileMissing: string[];
        catalogMissing: string[];
      }>
    ) => {
      const { profileMissing, catalogMissing } = action.payload;

      profileMissing.forEach((customerId) => {
        if (state.catalogDownloadStatus[customerId]) {
          state.catalogDownloadStatus[customerId].profile = false;
        }
      });

      catalogMissing.forEach((customerId) => {
        if (state.catalogDownloadStatus[customerId]) {
          state.catalogDownloadStatus[customerId].catalog = false;
        }
      });

      // If any profiles were reset, the "all synced" flag is no longer valid
      if (profileMissing.length > 0) {
        state.allCustomerProfileSynced = false;
      }
    },
  },
});

// ─── Actions ──────────────────────────────────────────────────────────────────

export const downloadStatusReducer = downloadStatusSlice.reducer;

export const {
  addCatalogDownloadItems,
  updateCatalogDownloadStatus,
  updateCustomerProfileDownloadStatus,
  updateAllCustomerProfileSynced,
  updateAllCustomerGroupProfileSynced,
  resetDownloadStatusForCustomers,
} = downloadStatusSlice.actions;

// ─── Selectors ────────────────────────────────────────────────────────────────

const getDownloadStatusSliceState = (
  rootState: RootState
): DownloadStatusState => rootState[DOWNLOAD_STATUS_FEATURE_KEY];

export const getCatalogDownloadStatus = createSelector(
  getDownloadStatusSliceState,
  (state) => state.catalogDownloadStatus
);

/** Returns the full { catalog, profile } object for a given customerId */
export const getCatalogAndProfileDownloadStatusForCustomer = createSelector(
  getDownloadStatusSliceState,
  (state) => (customerId: string) => state.catalogDownloadStatus[customerId]
);

/** Returns only the catalog boolean for a given customerId */
export const getCatalogDownloadStatusForCustomer = createSelector(
  getDownloadStatusSliceState,
  (state) => (customerId: string) =>
    state.catalogDownloadStatus[customerId]?.catalog ?? false
);

/** Returns only the profile boolean for a given customerId */
export const getCustomerProfileSyncStatusForCustomer = createSelector(
  getDownloadStatusSliceState,
  (state) => (customerId: string) =>
    state.catalogDownloadStatus[customerId]?.profile ?? false
);

/** True only when every tracked customer has catalog: true */
export const allCustomerCatalogDownloadStatus = createSelector(
  getDownloadStatusSliceState,
  (state) =>
    Object.values(state.catalogDownloadStatus).every((x) => x.catalog === true)
);

/** True only when every tracked customer has profile: true */
export const getAllCustomerProfileSynced = createSelector(
  getDownloadStatusSliceState,
  (state) =>
    Object.values(state.catalogDownloadStatus).every((x) => x.profile === true)
);

export const getAllCustomerGroupProfileSynced = createSelector(
  getDownloadStatusSliceState,
  (state) => state.allCustomerGroupProfileSynced
);

/** Count of customers currently being tracked */
export const getAvailableCustomersCount = createSelector(
  getDownloadStatusSliceState,
  (state) => Object.keys(state.catalogDownloadStatus).length
);
